<template>
  <nav
    class="cnav"
    :class="`cnav--${mode}`"
    :aria-label="t('nav.aria')"
    :data-testid="mode === 'phone' ? 'app-tab-bar' : 'app-sidebar'"
  >
    <div v-if="mode === 'desktop'" class="cnav__brand" aria-hidden="true">TACITA</div>
    <button
      v-for="item in items"
      :key="item.id"
      type="button"
      class="cnav__item"
      :class="{ 'cnav__item--active': item.id === current }"
      :data-testid="'tab-' + item.id"
      :aria-current="item.id === current ? 'page' : undefined"
      @click="$emit('select', item.id)"
    >
      <span class="cnav__icon">
        <component :is="item.icon" :size="mode === 'desktop' ? 20 : 22" />
        <span v-if="item.dot" class="cnav__dot" :aria-label="t('nav.unread')" />
      </span>
      <span class="cnav__label">{{ item.label }}</span>
    </button>
  </nav>
</template>

<script setup lang="ts">
import type { Component } from 'vue';
import { useI18n } from 'vue-i18n';
import type { LayoutMode } from '@/composables/useLayout';

export interface CabinetNavItem {
  id: string;
  label: string;
  icon: Component;
  /** An unread-message dot on the icon (the concierge item). */
  dot?: boolean;
}

defineProps<{ items: CabinetNavItem[]; current: string; mode: LayoutMode }>();
defineEmits<{ select: [id: string] }>();
const { t } = useI18n();
</script>

<style scoped lang="scss">
.cnav {
  display: flex;
  font-family: var(--font-body);
}

.cnav__item {
  display: flex;
  align-items: center;
  color: var(--muted);
  position: relative;
  transition: color var(--dur-fast) var(--ease-out), background var(--dur-fast) var(--ease-out);
}

.cnav__icon { position: relative; display: inline-flex; }

.cnav__dot {
  position: absolute;
  top: -1px;
  right: -3px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--red);
  box-shadow: 0 0 0 2px var(--cream);
}

/* Phone: bottom tab bar, 3 items, an active line above the icon. */
.cnav--phone {
  justify-content: space-around;
  align-items: stretch;
  padding: 10px 10px max(14px, env(safe-area-inset-bottom));
  background: var(--cream);
  border-top: 1px solid var(--line);
  box-shadow: 0 -8px 30px #14110d06;

  .cnav__item {
    flex: 1 1 0;
    min-width: 0;
    flex-direction: column;
    justify-content: center;
    gap: 4px;
    min-height: 46px;
  }
  .cnav__item--active { color: var(--bronze-text); }
  .cnav__item--active::before {
    content: '';
    position: absolute;
    top: -10px;
    width: 18px;
    height: 2px;
    border-radius: 2px;
    background: var(--bronze);
  }
  .cnav__label { font-size: 11px; white-space: nowrap; }
}

/* Tablet: a 92 px rail, icon above the label. */
.cnav--rail {
  flex-direction: column;
  gap: 8px;
  height: 100%;
  width: var(--rail-w);
  padding: 22px 9px;
  background: var(--sidebar);
  border-right: 1px solid var(--line);

  .cnav__item {
    flex-direction: column;
    justify-content: center;
    gap: 6px;
    min-height: 70px;
    border-radius: 14px;
    text-align: center;
  }
  .cnav__label { font-size: 11px; line-height: 1.2; }
}

/* Desktop: a 216 px sidebar with the wordmark. */
.cnav--desktop {
  flex-direction: column;
  gap: 6px;
  height: 100%;
  width: var(--sidebar-w);
  padding: 26px 18px;
  background: var(--sidebar);
  border-right: 1px solid var(--line);

  .cnav__item {
    gap: 12px;
    min-height: 46px;
    padding: 0 14px;
    border-radius: 12px;
    font-size: 14px;
  }
}

.cnav__brand {
  font-family: var(--font-heading);
  font-size: 16px;
  letter-spacing: 4px;
  color: var(--onyx);
  padding: 0 14px 22px;
}

.cnav--rail .cnav__item--active,
.cnav--desktop .cnav__item--active {
  background: var(--soft);
  color: var(--bronze-text);
}
.cnav--rail .cnav__item:hover,
.cnav--desktop .cnav__item:hover { background: var(--soft); }
</style>
